import { ReactElement, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "../../InternationalSolutionsPage.module.css";
import { Box, Button } from "@justworkshr/milo-core";
import {
  CF_ADD_EMPLOYEES_LINK,
  EOR_SWITCHING_HELP_LINK,
} from "pages/international-solutions/constants";
import { oktaAuth, pkceIsEnabled } from "app/okta";
import {
  useGetCompanyDisplayQuery,
  useGetIsEmployerOfRecordEnabledQuery,
} from "types/generated/operations";
import {
  CUSTOMER_IO_CTA_CLICK_EVENT,
  sendCustomerIOEvent,
} from "app/customer-io";
import { FLAG_NAMES, flagIsEnabled } from "lib/environment";
import ThankYouDialog from "../Dialog/ThankYouDialog";

const EORPromoPriceCard = (): ReactElement => {
  const { divWrapper, mdGap, mdPadding, mdHead, xsPadding, smFont, hr, smGap } =
    styles;
  const [searchParams, setSearchParams] = useSearchParams();

  const [showThankYouDialog, setShowThankYouDialog] = useState(false);

  const clearSearchParams = () => {
    if (searchParams.has("thankyou")) {
      searchParams.delete("thankyou");
      setSearchParams(searchParams);
    }
  };

  const handleThankYouDialog = () => {
    setShowThankYouDialog((prev) => !prev);
    clearSearchParams();
  };

  const { data: eorEnabledData, loading } =
    useGetIsEmployerOfRecordEnabledQuery();
  const isEmployerOfRecordEnabled =
    eorEnabledData?.authenticatedMember.company.companySetting?.value ===
      "true" && flagIsEnabled(FLAG_NAMES.EMPLOYER_OF_RECORD);

  const accessToken = pkceIsEnabled() ? oktaAuth.getAccessToken() : "";
  const { data: memberData } = useGetCompanyDisplayQuery();

  const triggerCustomerIOEvent = (ctaName: string, nextUrl: string) => {
    sendCustomerIOEvent(
      {
        event_type: CUSTOMER_IO_CTA_CLICK_EVENT,
        member_uuids: [memberData?.authenticatedMember.uuid ?? ""],
        company_uuids: [memberData?.authenticatedMember.company.uuid ?? ""],
        data: {
          current_url: window.location.href,
          cta_name: ctaName,
          next_url: nextUrl,
        },
      },
      accessToken
    );
  };

  useEffect(() => {
    if (searchParams.get("thankyou") === "true") {
      setShowThankYouDialog(true);
      triggerCustomerIOEvent(
        "eor_eor_promo_im_interested_button",
        "thank-you-modal"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <Box
      backgroundColor="neutral"
      border={{
        borderColor: "neutralSubtle",
        borderRadius: "md",
        borderWidth: "md",
      }}
      padding="md"
    >
      <div className={`${divWrapper} ${mdGap}`}>
        <div className={`${divWrapper} ${xsPadding} ${smGap}`}>
          <div className={mdHead}>Deal Pricing</div>
          <div className={smFont}>
            $299 per employee per month (reduced from $599) for the first 12
            months. Fees apply for each EOR employee per month.
          </div>
        </div>
        <hr className={hr}></hr>
        <div className={`${divWrapper} ${mdPadding} ${smGap}`}>
          <div className={mdHead}>Expanded EOR Coverage</div>
          <div className={smFont}>
            Justworks offers EOR services in top talent markets like Israel,
            Philippines, France, Italy, Canada, Mexico, the U.K., and India.
          </div>
        </div>
        <hr className={hr}></hr>
        <div className={`${divWrapper} ${mdPadding} ${smGap}`}>
          <div className={mdHead}>Resources</div>
          <div className={smFont}>
            See how easy it is to swap your old provider for Justworks EOR with
            our brief guide:{" "}
            <a href={EOR_SWITCHING_HELP_LINK} target="_blank">
              <span className={smFont}>
                Switching to Justworks EOR Hassle-Free
              </span>
            </a>
          </div>
        </div>
        {isEmployerOfRecordEnabled ? (
          <Button
            data-heap-id="eor-add-employees-button"
            color="brand"
            type="button"
            size="md"
            as="a"
            href={CF_ADD_EMPLOYEES_LINK}
            onClick={() => {
              triggerCustomerIOEvent(
                "eor_eor_promo_add_employee_button",
                CF_ADD_EMPLOYEES_LINK
              );
            }}
          >
            Get started
          </Button>
        ) : (
          <Button
            data-heap-id="no-eor-im-interested-button"
            color="brand"
            type="button"
            size="md"
            onClick={() => {
              triggerCustomerIOEvent(
                "eor_eor_promo_im_interested_button",
                "thank-you-modal"
              );
              handleThankYouDialog();
            }}
            loading={loading}
          >
            Get started
          </Button>
        )}
      </div>
      {showThankYouDialog && (
        <ThankYouDialog
          isOpen={showThankYouDialog}
          onClose={handleThankYouDialog}
        />
      )}
    </Box>
  );
};

export default EORPromoPriceCard;
